import React from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { ENGINE_DEFAULTS } from '../Table.constants';
import DefaultReactTable from './styled';

const extendedTables = new WeakMap();

/**
 * Engine base table extended with the schema `styles` (a `css` fragment), exactly like a local
 * `styled(CustomReactTable)` did: same rules, same order. Each extension is created once per
 * styles fragment so it keeps a stable component identity.
 *
 * @param {Array} [styles] - styled-components `css` fragment
 * @return {Object} styled engine table
 */
export const getBaseTable = (styles) => {
  if (!styles) return DefaultReactTable;
  if (!extendedTables.has(styles)) extendedTables.set(styles, styled(DefaultReactTable)`${styles}`);

  return extendedTables.get(styles);
};

const toFetchData = (onFetch) => onFetch && ((engineState) => onFetch(engineState.sorted, engineState));
const hideTableHeader = () => null;

/**
 * Row props for the engine: the view's own `getRowProps`, plus the selected-row background
 * when the view tells which rows are selected (`isRowSelected`).
 *
 * @param {Function} [getRowProps] - `(rowInfo) => props`
 * @param {Function} [isRowSelected] - `(rowInfo) => boolean`
 * @param {string} selectedBackground - theme color of a selected row
 * @return {Function|undefined} engine `getTrProps`
 */
export const toTrProps = (getRowProps, isRowSelected, selectedBackground) => {
  if (!getRowProps && !isRowSelected) return undefined;

  return (_, rowInfo) => {
    const rowProps = getRowProps ? getRowProps(rowInfo) : {};
    if (!isRowSelected || !rowInfo?.row) return rowProps;

    return {
      ...rowProps,
      style: { ...rowProps.style, background: isRowSelected(rowInfo) ? `${selectedBackground}` : '' }
    };
  };
};
const toTheadThProps = (getHeaderProps) => getHeaderProps && ((_, __, column) => getHeaderProps(column));

const ReactTable6 = ({
  styles = undefined,
  onFetch = undefined,
  onResize = undefined,
  getRowProps = undefined,
  isRowSelected = undefined,
  getHeaderProps = undefined,
  hideHeader = false,
  ...engineProps
}) => {
  const BaseTable = getBaseTable(styles);
  const theme = useTheme();
  const hideHeaderProps = hideHeader ? { TheadComponent: hideTableHeader } : {};

  return (
    <BaseTable
      { ...ENGINE_DEFAULTS }
      onFetchData={ toFetchData(onFetch) }
      onResizedChange={ onResize }
      getTrProps={ toTrProps(getRowProps, isRowSelected, theme?.backgrounds?.selectedTableItem) }
      getTheadThProps={ toTheadThProps(getHeaderProps) }
      { ...hideHeaderProps }
      { ...engineProps }
    />
  );
};

ReactTable6.propTypes = {
  styles: PropTypes.array,
  onFetch: PropTypes.func,
  onResize: PropTypes.func,
  getRowProps: PropTypes.func,
  isRowSelected: PropTypes.func,
  getHeaderProps: PropTypes.func,
  hideHeader: PropTypes.bool
};

export default ReactTable6;
